/**
 * M2 onboarding: shared validation and API wrappers for org setup.
 * Uses getAuthToken + ACCOUNTS_BACKEND for ein-lookup, website-enrich, create-org.
 */

import { getAuthToken } from '../../../utils/auth';
import { accountsClient } from '../../../lib/trpc/accounts';

/** Input class — dashboard FormInput styling with white bg and warm-brown border */
export const INPUT_CLASS =
  'w-full h-8 bg-paper-light rounded-md border border-edge px-3 text-sm text-ink placeholder:text-ink-muted shadow-none focus:outline-none focus:ring-2 focus:ring-secondary focus:border-secondary';
/** Textarea class — dashboard FormTextarea styling with white bg */
export const TEXTAREA_CLASS =
  'w-full bg-paper-light rounded-md border border-edge resize-none py-1.5 px-2 text-sm placeholder:text-edge shadow-none focus:outline-none focus:ring-2 focus:ring-secondary focus:border-secondary';
/**
 * Select surface — extends INPUT_CLASS so border/bg/text match the URL and other fields.
 * Use with `!value && 'text-ink-muted'` (or equivalent) so the empty option reads like a placeholder.
 */
export const SELECT_CLASS = `${INPUT_CLASS} py-0 pr-10 leading-8 appearance-none cursor-pointer`;
export const INPUT_READONLY_CLASS =
  'w-full h-8 bg-paper-light rounded-md border border-edge px-3 text-sm text-ink-secondary';

/** Radio input class to match dashboard (FundraiserFormConfigurationTab) */
export const RADIO_CLASS =
  'h-4 w-4 cursor-pointer rounded-full bg-paper-muted focus:ring-sage border-edge text-ink-muted';

export function validateOrgName(name) {
  const t = (name || '').trim();
  return t.length >= 1 && t.length <= 200;
}

export function validateUrl(url) {
  if (!url || !url.trim()) return true;
  try {
    const u = new URL(url.startsWith('http') ? url : `https://${url}`);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

/** Normalize optional website input for API calls (https default). */
export function normalizeWebsiteUrl(raw) {
  const t = (raw || '').trim();
  if (!t) return '';
  return t.startsWith('http') ? t : `https://${t}`;
}

export function validateEIN(ein) {
  if (!ein || !ein.trim()) return false;
  const digits = ein.replace(/\D/g, '');
  return digits.length === 9;
}

export function formatEIN(ein) {
  const d = (ein || '').replace(/\D/g, '').slice(0, 9);
  if (d.length < 9) return d;
  return `${d.slice(0, 2)}-${d.slice(2, 9)}`;
}

/**
 * POST /ein-lookup with { orgName } or { ein }. Returns { found, results?, legalName, ein, ... } or { found: false }.
 * When searching by orgName, results is an array of matches for dropdown.
 */
export async function callEINLookup(payload) {
  console.log('[Onboarding] callEINLookup', payload);
  const token = await getAuthToken();

  const query = (payload?.query || '').trim();
  const normalizedPayload = { ...payload };

  // Backward compatibility: some deployed backends still expect orgName/ein instead of query.
  if (query && !normalizedPayload.orgName && !normalizedPayload.ein) {
    const queryDigits = query.replace(/\D/g, '');
    if (queryDigits.length === 9) {
      normalizedPayload.ein = queryDigits;
    } else {
      normalizedPayload.orgName = query;
    }
  }

  const result = await accountsClient.requestJson('POST /ein-lookup', {
    authorization: token,
    input: normalizedPayload,
  });
  // requestJson returns { ok, data, error }; API body (found, results, legalName, ...) is in data
  const body = result?.ok ? result.data : { found: false, error: result?.error };
  console.log('[Onboarding] callEINLookup result', body?.found ? { found: true, resultsCount: body.results?.length, legalName: body.legalName } : body);
  return body;
}

/**
 * POST /website-enrich with { url }. Returns { ok, ogImage, ogDescription, description, themeColor, favicon }.
 */
export async function callWebsiteEnrich(payload) {
  console.log('[Onboarding] callWebsiteEnrich', payload);
  const token = await getAuthToken();
  const result = await accountsClient.requestJson('POST /website-enrich', {
    authorization: token,
    input: payload,
  });
  console.log('[Onboarding] callWebsiteEnrich result', result?.ok ? 'ok' : result);
  return result;
}

/**
 * Check exact organization-name and slug availability before leaving Step 1.
 */
export async function callOrgAvailability(payload) {
  const token = await getAuthToken();
  const result = await accountsClient.requestJson('POST /onboarding/org-availability', {
    authorization: token,
    input: payload,
  });

  if (!result?.ok) {
    throw new Error(result?.error || 'Organization availability check failed');
  }

  return result.data;
}

/**
 * POST /onboarding/create-org with full payload. Returns { organizationId, clerkOrgId, activationPath }.
 */
export async function callCreateOrg(payload, { idempotencyKey } = {}) {
  console.log('[Onboarding] callCreateOrg', { ...payload, orgName: payload.orgName || payload.legalName });
  const token = await getAuthToken();
  // Preserve the same standard idempotency key for all request retries.
  // retryOn5xx handles transient 500/502/503 from Lambda cold starts or Stripe timeouts.
  const requestInput = { ...payload, ...(idempotencyKey ? { idempotencyKey } : {}) };
  const retryConfig = {
    context: 'create-org',
    retries: 2,
    retryDelay: 2000,
    backoff: 'exponential',
    retryOn5xx: true,
  };
  let result = await accountsClient.requestJson(
    'POST /onboarding/create-org',
    { authorization: token, input: requestInput },
    retryConfig,
  );
  // A second click/tab may be performing this exact idempotent request. Poll
  // briefly with the same UUID rather than making the user restart the wizard.
  for (let attempt = 0; result?.status === 409 && attempt < 3; attempt += 1) {
    const retryAfterSeconds = Number.parseInt(result.retryAfter, 10);
    const delay = Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
      ? retryAfterSeconds * 1000
      : 3000;
    await new Promise((resolve) => setTimeout(resolve, delay));
    const freshToken = await getAuthToken();
    result = await accountsClient.requestJson(
      'POST /onboarding/create-org',
      { authorization: freshToken, input: requestInput },
      retryConfig,
    );
  }
  console.log('[Onboarding] callCreateOrg result', result?.organizationId ? { organizationId: result.organizationId } : result);
  return result;
}
